import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ChatSidebar from "./components/chat/ChatSidebar";
import ChatHeader from "./components/chat/ChatHeader";
import ChatMessages from "./components/chat/ChatMessages";
import ChatInput from "./components/chat/ChatInput";
import MentiAvatar from './images/menti_bot.png'
import "./styles/Chat.css";

interface Conversation {
  id: string | null;
  title: string | null;
  messages: { role: string; content: string }[];
}

interface EducationalContext {
  classId: number;
  subjectId: number;
  seriesId: number;
  chapterId: number;
  topicId: number | null;
  className?: string;
  subjectName?: string;
  seriesName?: string;
  chapterName?: string;
  topicName?: string | null;
}

interface StreamEvent {
  event: string;
  data: any;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
console.log(process.env.REACT_APP_API_URL);
const mojibakePattern = /à¤|à¥|Ã|Â/;

const repairMojibake = (text: string) => {
  if (!text || !mojibakePattern.test(text)) return text;

  try {
    const bytes = Uint8Array.from(Array.from(text), char => char.charCodeAt(0) & 0xff);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return text;
  }
};

const mathDelimiterPattern = /\\\(|\\\)|\\\[|\\\]/;

// Models often emit ChatGPT-style \( \) / \[ \] delimiters instead of the
// requested $ / $$. remark-math only recognizes $ / $$, so without this the
// math never parses. Must run on the full accumulated message (not a single
// streamed chunk) since the model's tokenizer can split "\)" or "\]" across
// chunk boundaries in a way that never lands whole inside one delta.
const normalizeMathDelimiters = (text: string) => {
  if (!text || !mathDelimiterPattern.test(text)) return text;

  return text
    .replace(/\\\(/g, '$')
    .replace(/\\\)/g, '$')
    .replace(/\\\[/g, '$$')
    .replace(/\\\]/g, '$$');
};

const parseStreamEvents = (buffer: string): { events: StreamEvent[]; remaining: string } => {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  const remaining = parts.pop() || '';

  const events = parts.map(part => {
    const lines = part.split('\n');
    const event = lines.find(line => line.startsWith('event: '))?.slice(7).trim() || 'message';
    const dataText = lines
      .filter(line => line.startsWith('data: '))
      .map(line => line.slice(6))
      .join('\n');

    try {
      return { event, data: JSON.parse(dataText) };
    } catch {
      return { event, data: dataText };
    }
  });

  return { events, remaining };
};

function Chat() {
  const location = useLocation();
  const context = location.state as EducationalContext | undefined;

  const [message, setMessage] = useState('');
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [documents, setDocuments] = useState<string[]>([]);
  const [selectedDocument, setSelectedDocument] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const historyRef = useRef<{ fetchHistory: () => void }>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (context) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents`);
      if (!response.ok) return;
      const data = await response.json();
      setDocuments(data.documents || []);
      if (selectedDocument && !(data.documents || []).includes(selectedDocument)) {
        setSelectedDocument('');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [context, selectedDocument]);

  const setActiveConversationId = useCallback((conversationId: string) => {
    setCurrentConversation(prev => prev ? { ...prev, id: conversationId } : prev);
  }, []);

  const updateLastAssistantMessage = useCallback((content: string) => {
    setCurrentConversation(prev => {
      if (!prev) return prev;
      const newMessages = [...prev.messages];
      newMessages[newMessages.length - 1] = { role: 'assistant', content };
      return { ...prev, messages: newMessages };
    });
  }, []);

  useEffect(() => {
    // When currentConversation changes, if it has an id, it means it's saved.
    // We can potentially refresh the history list.
    if (currentConversation?.id) {
      historyRef.current?.fetchHistory();
    }
  }, [currentConversation?.id]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSendMessage = async () => {
    if (!message.trim() || isGenerating) return;

    const userMessage = { role: 'user', content: message };
    const outboundMessage = message;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setMessage('');
    setIsGenerating(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
        body: JSON.stringify({
          message: outboundMessage,
          conversation_id: currentConversation?.id,
          source_document: context ? "null" : (selectedDocument || null),
          ...(context && !currentConversation?.id
            ? {
                class_id: context.classId,
                subject_id: context.subjectId,
                series_id: context.seriesId,
                chapter_id: context.chapterId,
                topic_id: context.topicId ?? null,
              }
            : {}),
        }),
      });

      if (!response.ok) {
        throw new Error(`Chat request failed with status ${response.status}`);
      }

      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let botMessage = '';
        let buffer = '';

        setCurrentConversation(prev => ({
          id: prev?.id || null,
          title: prev?.title || null,
          messages: [...(prev?.messages || []), userMessage, { role: 'assistant', content: '' }]
        }));

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            historyRef.current?.fetchHistory();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const parsed = parseStreamEvents(buffer);
          buffer = parsed.remaining;

          for (const { event, data } of parsed.events) {
            if (event === 'conversation' && data?.id) {
              setActiveConversationId(data.id);
              continue;
            }

            if (event === 'token') {
              botMessage += data?.content || '';
              updateLastAssistantMessage(normalizeMathDelimiters(repairMojibake(botMessage)));
              console.log(JSON.stringify(botMessage));
              continue;
            }

            if (event === 'error') {
              const errorMessage = data?.message || 'LLM response failed.';
              updateLastAssistantMessage(errorMessage);
            }

            if (event === 'done') {
              historyRef.current?.fetchHistory();
            }
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        setCurrentConversation(prev => {
          if (!prev) return prev;
          const newMessages = [...prev.messages];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.role === 'assistant') {
            newMessages[newMessages.length - 1] = {
              role: 'assistant',
              content: `${lastMessage.content}\n\n[Stopped by user]`,
            };
          }
          return { ...prev, messages: newMessages };
        });
      } else {
        console.error('Error sending message:', error);
        updateLastAssistantMessage('Error sending message. Please try again.');
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  };

  const stopGeneration = () => {
    abortControllerRef.current?.abort();
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setCurrentConversation(conversation);
  };

  const startNewChat = () => {
    stopGeneration();
    setCurrentConversation(null);
  }

  const handleConversationRemove = (conversationId: string) => {
    if (currentConversation?.id === conversationId) {
      setCurrentConversation(null);
    }
  };

  const removeSelectedDocument = async () => {
    if (!selectedDocument) return;

    try {
      await fetch(`${API_BASE_URL}/api/documents/${encodeURIComponent(selectedDocument)}`, {
        method: 'DELETE',
      });
      setSelectedDocument('');
      fetchDocuments();
    } catch (error) {
      console.error('Error removing document:', error);
    }
  };

  const getDisplayHistory = () => {
    if (currentConversation) {
      return currentConversation.messages.map((msg, index) => ({
        type: msg.role === 'user' ? 'user' : 'bot',
        text: normalizeMathDelimiters(repairMojibake(msg.content))
      }));
    }
    return [];
  }

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [currentConversation]);

 return (
  <div className="chat-app">

    <ChatSidebar
      context={context}
      historyRef={historyRef}
      startNewChat={startNewChat}
      fetchDocuments={fetchDocuments}
      documents={documents}
      selectedDocument={selectedDocument}
      setSelectedDocument={setSelectedDocument}
      removeSelectedDocument={removeSelectedDocument}
      onConversationSelect={handleSelectConversation}
      onConversationRemove={handleConversationRemove}
    />

    <main className="chat-main">

      <ChatHeader
        context={context}
        conversationTitle={currentConversation?.title}
      />

      <ChatMessages
        messages={getDisplayHistory()}
        onSuggestionClick={setMessage}
      />

      {isGenerating && (

        <div className="typing-container">

          <div className="typing-card">

            <div className="typing-avatar">
              <img src={MentiAvatar} alt="Menti AI" width="50"/>
            </div>

            <div className="typing-loader">

              <span></span>
              <span></span>
              <span></span>

            </div>

            <button
              className="stop-btn"
              onClick={stopGeneration}
            >
              Stop
            </button>

          </div>

        </div>

      )}

      <ChatInput
        value={message}
        loading={isGenerating}
        onChange={setMessage}
        onSend={handleSendMessage}
      />

    </main>

  </div>
);
}

export default Chat;
